import {
  AddOutlined,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowRight as ArrowRightIcon,
  Cached as CachedIcon,
  DeleteOutline,
  FileUpload as FileUploadIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Grid,
  IconButton,
  Paper,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'src/components/menu/Menu';
import { ComponentListModal } from 'src/components/modal';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { lang } from 'src/utils/language';

import * as Style from '../CustomerTriggeredEventsPage.styled';
import {
  getComponentOrder,
  getSuggestedComponentTypes,
  handleAddComponentClick,
  handleAddComponentConfirmed,
  handleDaypartChange,
  handleSwapComponentConfirmed,
  isRowValid,
  toggleExpand,
} from '../hooks/useCustomerTriggeredEventsTable.helper';
import { useCustomerTriggeredEventsTable } from '../hooks/useCustomerTriggeredEventsTable.hook';
import { CustomerTriggeredEventsTableProps } from './CustomerTriggeredEventsTable.interface';

export const CustomerTriggeredEventsTable: React.FC<
  CustomerTriggeredEventsTableProps
> = ({ rows, setRows, dayparts, isViewOnly = false }) => {
  const {
    getFiltersForRow,
    handleMenuOpen,
    handleDeleteComponent,
    handleMenuClose,
    menuAnchorEl,
    componentModal,
    handleCloseComponentModal,
    openComponentModal,
    handleSwapComponent,
    componentPosition,
    setComponentPosition,
  } = useCustomerTriggeredEventsTable({
    rows,
    setRows,
  });
  // Function to check if all dayparts are assigned for a row
  const areAllDaypartsAssigned = (row: any, totalDayparts: number) => {
    const assignedDaypartIds = new Set(
      row.components?.flatMap((c: { selectedDayparts: any[] }) =>
        c.selectedDayparts?.map((dp) => dp.daypart_id),
      ) ?? [],
    );

    return assignedDaypartIds.size === totalDayparts;
  };

  return (
    <Style.SectionBox>
      <Style.TableWrapper component={Paper} elevation={0}>
        <Grid container spacing={0} sx={{ width: '100%' }}>
          <Grid item xs={12}>
            <Style.MainTable>
              <TableHead>
                <TableRow>
                  <Style.EventsHeaderCell>Events</Style.EventsHeaderCell>
                  <Style.SwapHeaderCell />
                  <Style.ThemeHeaderCell>Theme</Style.ThemeHeaderCell>
                  {dayparts.map((dp) => (
                    <Style.DaypartHeaderCell key={dp.id}>
                      {dp.name}
                    </Style.DaypartHeaderCell>
                  ))}
                  <Style.ActionHeaderCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => (
                  <React.Fragment key={r.id}>
                    <Style.EventHeaderRow $selected={r.selected}>
                      <Style.EventHeaderCell $selected={r.selected}>
                        <Style.EventHeaderCellContent>
                          {isRowValid(r, dayparts.length) ? (
                            <Style.StyledCheckCircleIcon fontSize="small" />
                          ) : (
                            <CustomTooltip
                              title={
                                !r.components?.length ||
                                !areAllDaypartsAssigned(r, dayparts.length)
                                  ? lang.customerTriggeredEventsPage
                                      .noComponentTooltip
                                  : ''
                              }
                              placement="left"
                              isSecondaryTooltip={false}
                              skipValidateOverflow={true}>
                              <Style.NoComponentIcon>
                                <Style.StyledErrorOutlineRoundedIcon fontSize="small" />
                              </Style.NoComponentIcon>
                            </CustomTooltip>
                          )}
                          <Style.ExpandIconButton
                            size="small"
                            onClick={() => toggleExpand(r.id, setRows)}>
                            {r.expanded ? (
                              <ArrowDropDownIcon />
                            ) : (
                              <ArrowRightIcon />
                            )}
                          </Style.ExpandIconButton>
                          <Style.ThemeMarker
                            style={{ backgroundColor: r.themeColor }}
                          />
                          <Style.EventNameTypography>
                            {r.name}
                          </Style.EventNameTypography>
                        </Style.EventHeaderCellContent>
                      </Style.EventHeaderCell>
                      <Style.EmptyEventTableCellSwap $selected={r.selected} />
                      <Style.EmptyEventTableCellTheme $selected={r.selected} />
                      {dayparts.map((dp) => (
                        <Style.EmptyEventTableCellDaypart
                          key={dp.id}
                          $selected={r.selected}
                        />
                      ))}
                      <Style.EmptyEventTableCellAction $selected={r.selected} />
                    </Style.EventHeaderRow>

                    {r.expanded && (
                      <>
                        {(r.components ?? [])
                          .slice(0, dayparts.length)
                          .map((c) => (
                            <Style.ComponentRow key={c.id}>
                              <Style.InnerCellEvents>
                                <Style.InnerFirstBox>
                                  <CustomTooltip
                                    width={500}
                                    placement="bottom"
                                    title={c.name}
                                    overflowWidth={144}>
                                    <Style.ComponentLink
                                      component={Link}
                                      to={PATHS.COMPONENT_DETAILS(Number(c.id))}
                                      target="_blank"
                                      underline="always">
                                      {c.name}
                                    </Style.ComponentLink>
                                  </CustomTooltip>
                                </Style.InnerFirstBox>
                              </Style.InnerCellEvents>
                              <Style.InnerCellSwap align="center">
                                <Style.SwapButton
                                  size="small"
                                  disabled={isViewOnly}
                                  onClick={() =>
                                    handleSwapComponent(r.id, c.id)
                                  }
                                  title={
                                    lang.customerTriggeredEventsPage
                                      .swapComponent
                                  }>
                                  <CachedIcon fontSize="small" />
                                </Style.SwapButton>
                              </Style.InnerCellSwap>
                              <Style.InnerCellTheme align="left">
                                <Typography variant="body2">
                                  {c.theme || ''}
                                </Typography>
                              </Style.InnerCellTheme>

                              {dayparts.map((dp) => {
                                const isSelected = !!c.selectedDayparts?.find(
                                  (daypart) => daypart.daypart_id === dp.id,
                                );

                                return (
                                  <Style.InnerCellDaypart
                                    key={dp.id}
                                    align="center">
                                    <Style.StyledCheckbox
                                      checked={isSelected}
                                      disabled={isViewOnly}
                                      $isViewOnly={isViewOnly}
                                      onChange={() =>
                                        handleDaypartChange(
                                          r.id,
                                          c.id,
                                          dp.id,
                                          setRows,
                                        )
                                      }
                                      icon={
                                        <Style.CircularUncheckedIcon
                                          $isViewOnly={isViewOnly}
                                        />
                                      }
                                      checkedIcon={
                                        <Style.CircularCheckedIcon
                                          $isViewOnly={isViewOnly}
                                        />
                                      }
                                      inputProps={{
                                        'aria-label': `${c.name}-${dp.id}`,
                                      }}
                                    />
                                  </Style.InnerCellDaypart>
                                );
                              })}
                              <Style.InnerCellAction align="center">
                                {!isViewOnly && (
                                  <>
                                    <IconButton
                                      size="small"
                                      onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>,
                                      ) => handleMenuOpen(e, r.id, c.id)}>
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                    <Menu
                                      anchorEl={
                                        menuAnchorEl[`${r.id}_${c.id}`] || null
                                      }
                                      items={[
                                        ...((r.components?.length || 0) <
                                        dayparts.length
                                          ? [
                                              {
                                                label:
                                                  lang
                                                    .customerTriggeredEventsPage
                                                    .addComponent,
                                                onClick: () => {
                                                  setComponentPosition(
                                                    getComponentOrder(r),
                                                  );
                                                  handleMenuClose(
                                                    `${r.id}_${c.id}`,
                                                  );
                                                  openComponentModal(r.id);
                                                },
                                                leftIcon: <AddOutlined />,
                                              },
                                            ]
                                          : []),
                                        {
                                          label:
                                            lang.customerTriggeredEventsPage
                                              .deleteComponent,
                                          onClick: () => {
                                            handleMenuClose(`${r.id}_${c.id}`);
                                            handleDeleteComponent(r.id, c.id);
                                          },
                                          leftIcon: <DeleteOutline />,
                                        },
                                      ]}
                                      onClose={() =>
                                        handleMenuClose(`${r.id}_${c.id}`)
                                      }
                                    />
                                  </>
                                )}
                              </Style.InnerCellAction>
                            </Style.ComponentRow>
                          ))}

                        {r.expanded &&
                          (r.components?.length || 0) < dayparts.length &&
                          (r.components?.length || 0) === 0 &&
                          !isViewOnly && (
                            <Style.ComponentRow>
                              <Style.InnerCellEvents>
                                <Style.InnerFirstBox>
                                  <Style.ComponentLink
                                    component="button"
                                    onClick={(
                                      e: React.MouseEvent<HTMLButtonElement>,
                                    ) => {
                                      setComponentPosition(
                                        getComponentOrder(r),
                                      );
                                      handleAddComponentClick(
                                        e,
                                        r.id,
                                        rows,
                                        dayparts.length,
                                        openComponentModal,
                                      );
                                    }}>
                                    +{' '}
                                    {
                                      lang.customerTriggeredEventsPage
                                        .addComponent
                                    }
                                  </Style.ComponentLink>
                                </Style.InnerFirstBox>
                              </Style.InnerCellEvents>
                              <Style.InnerCellSwap align="center">
                                <Style.SwapButton
                                  onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>,
                                  ) =>
                                    handleAddComponentClick(
                                      e,
                                      r.id,
                                      rows,
                                      dayparts.length,
                                      openComponentModal,
                                    )
                                  }
                                  title={
                                    lang.customerTriggeredEventsPage
                                      .addComponent
                                  }>
                                  <FileUploadIcon fontSize="small" />
                                </Style.SwapButton>
                              </Style.InnerCellSwap>
                              <Style.InnerCellTheme></Style.InnerCellTheme>
                              {dayparts.map((dp) => (
                                <Style.InnerCellDaypart
                                  key={dp.id}
                                  align="center"></Style.InnerCellDaypart>
                              ))}
                              <Style.InnerCellAction></Style.InnerCellAction>
                            </Style.ComponentRow>
                          )}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Style.MainTable>
          </Grid>
        </Grid>
      </Style.TableWrapper>

      {componentModal.isOpen &&
        (() => {
          const currentRow = rows.find((r) => r.id === componentModal.rowId);
          const filters = currentRow ? getFiltersForRow(currentRow) : [];

          const suggestedComponentTypes = getSuggestedComponentTypes(
            currentRow?.componentVariant,
          );

          return (
            <ComponentListModal
              open={componentModal.isOpen}
              onClose={handleCloseComponentModal}
              filters={filters}
              suggestedComponentTypes={suggestedComponentTypes}
              infoMessage={
                lang.customerTriggeredEventsPage
                  .filteredToShowOnlyComponentsWithNoEndDate
              }
              buttonText={lang.selectComponent}
              onSelectedComponent={(component) =>
                componentModal.replaceCompId
                  ? handleSwapComponentConfirmed(
                      component,
                      componentModal.rowId,
                      setRows,
                      handleCloseComponentModal,
                      componentModal.replaceCompId,
                    )
                  : handleAddComponentConfirmed(
                      component,
                      componentModal.rowId,
                      setRows,
                      handleCloseComponentModal,
                      componentPosition,
                    )
              }
            />
          );
        })()}
    </Style.SectionBox>
  );
};
